import { Button, Modal, Spinner, Typography, Tooltip } from '@scale/scaleui';
import { useMutation, useQueryClient } from 'react-query';
import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  useGetAllUsersOnOCPStep,
  useGetWorkerTeamIdForCanonicalStepName,
} from 'react-scale/components/GrowthCenter/OnboardingControlPanel/api';

interface RescreenUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  stepName: string;
  flowId: string;
}

export const RescreenUsersModal: React.FC<RescreenUsersModalProps> = ({
  isOpen,
  onClose,
  stepName,
  flowId,
}) => {
  const queryClient = useQueryClient();
  const workerTeamIdQuery = useGetWorkerTeamIdForCanonicalStepName(stepName);
  const usersQuery = useGetAllUsersOnOCPStep(flowId, stepName, workerTeamIdQuery.data?.teamId);

  const numUsersFound = usersQuery.data?.length;
  const queriesLoading = usersQuery.isLoading || workerTeamIdQuery.isLoading;

  const { mutate, data, error, isLoading, isError } = useMutation<
    { message: string },
    { response?: { message: string }; message: string },
    { flowId: string }
  >(
    ({ flowId }) =>
      apiFetch(null, `corp-api/onboarding-control-panel/flow/${flowId}/${stepName}/rescreen`, {
        method: 'POST',
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'all-users-on-step',
          flowId,
          stepName,
          workerTeamIdQuery.data?.teamId,
        ]);
      },
    },
  );
  const handleSubmit = () => {
    mutate({ flowId });
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        if (!isLoading) {
          onClose();
        }
      }}
      title={`Rescreen Users in ${stepName}`}
      innerClassName="w-1/2"
    >
      {!queriesLoading ? (
        <Typography.Paragraph2>Non-fraud users found: {numUsersFound}</Typography.Paragraph2>
      ) : (
        <Spinner size="sm" />
      )}
      <div className="flex flex-row items-center justify-end">
        {isLoading && <Spinner size="sm" className="mr-4" />}
        <Tooltip
          side="left"
          target={
            <div>
              <Button
                onClick={handleSubmit}
                disabled={!numUsersFound || queriesLoading || isLoading}
              >
                Rescreen
              </Button>
            </div>
          }
          text={!numUsersFound ? 'No users to rescreen' : ''}
        />
      </div>
      {isError && error && (
        <p style={{ color: 'red' }} className="my-2 text-xs">
          Failed to rescreen users: {error.response?.message ?? error.message}
        </p>
      )}
      {data && (
        <Typography.Paragraph3 className="mt-4 mb-1">
          {data.message}.
          <br />
          <br />
          Please wait for a moment for users to transition through the flow and refresh for the
          updated flow.
        </Typography.Paragraph3>
      )}
    </Modal>
  );
};
